import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Copy, Eraser, CheckCircle2, CircleDashed, Flame, Camera } from "lucide-react";
import { toast } from "sonner";
import StickerChip from "@/components/StickerChip";
import FiguScanModal from "@/components/FiguScanModal";
import {
  useInventory,
  useToggleSticker,
  usePutInventory,
  parseRanges,
} from "@/lib/inventory";
import { formatApiErrorDetail } from "@/lib/api";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import { useStreak } from "@/lib/streak";
import { useI18n } from "@/lib/I18nContext";

const TOTAL = 650;
const ALL_NUMBERS = Array.from({ length: TOTAL }, (_, i) => i + 1);

export default function Inventory() {
  const { t } = useI18n();
  const { data, isLoading, isError, refetch } = useInventory();
  const toggle = useToggleSticker();
  const put = usePutInventory();

  const [activeTab, setActiveTab] = useState("duplicates");
  const [quickValue, setQuickValue] = useState("");
  const [quickError, setQuickError] = useState("");
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  // Phase 21 — FiguScan modal open state.
  const [scanOpen, setScanOpen] = useState(false);
  useOverlayLock(confirmClearOpen);
  useOverlayLock(scanOpen);
  const keyBufferRef = useRef("");
  const keyTimerRef = useRef(null);

  // Sets for O(1) state lookup during render
  const { duplicatesSet, missingSet } = useMemo(() => {
    return {
      duplicatesSet: new Set(data?.duplicates || []),
      missingSet: new Set(data?.missing || []),
    };
  }, [data]);

  const getState = useCallback(
    (n) => {
      if (duplicatesSet.has(n)) return "duplicate";
      if (missingSet.has(n)) return "missing";
      return "neither";
    },
    [duplicatesSet, missingSet]
  );

  const handleChipClick = useCallback(
    async (number) => {
      try {
        await toggle.mutateAsync({ number, list: activeTab });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    },
    [toggle, activeTab]
  );

  // Keyboard: typing a number and pressing Enter toggles it in the active tab
  useEffect(() => {
    function onKey(e) {
      const ae = document.activeElement;
      const tag = ae?.tagName;
      // Don't intercept when typing in inputs/textareas/contenteditable
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        ae?.getAttribute?.("contenteditable") === "true"
      ) {
        return;
      }
      if (/^\d$/.test(e.key)) {
        keyBufferRef.current += e.key;
        if (keyBufferRef.current.length > 3) {
          keyBufferRef.current = keyBufferRef.current.slice(-3);
        }
        clearTimeout(keyTimerRef.current);
        keyTimerRef.current = setTimeout(() => {
          keyBufferRef.current = "";
        }, 1500);
      } else if (e.key === "Enter" && keyBufferRef.current) {
        const n = Number(keyBufferRef.current);
        keyBufferRef.current = "";
        if (n >= 1 && n <= 650) {
          handleChipClick(n);
        }
      } else if (e.key === "Escape") {
        keyBufferRef.current = "";
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(keyTimerRef.current);
    };
  }, [handleChipClick]);

  async function handleQuickAdd(e) {
    e?.preventDefault?.();
    setQuickError("");
    const { numbers, invalid } = parseRanges(quickValue);
    if (invalid.length) {
      setQuickError(`Invalid: ${invalid.join(", ")}`);
      return;
    }
    if (!numbers.length || !data) return;

    const target = activeTab;
    const other = target === "duplicates" ? "missing" : "duplicates";
    const targetSet = new Set(data[target]);
    const otherSet = new Set(data[other]);
    for (const n of numbers) {
      targetSet.add(n);
      otherSet.delete(n);
    }
    const payload = {
      duplicates: target === "duplicates" ? [...targetSet] : [...otherSet],
      missing: target === "missing" ? [...targetSet] : [...otherSet],
    };
    try {
      await put.mutateAsync(payload);
      toast.success(
        `Added ${numbers.length} number${numbers.length === 1 ? "" : "s"} to ${target}`
      );
      setQuickValue("");
    } catch (err) {
      setQuickError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }

  async function handleClearList() {
    if (!data) return;
    const payload = {
      duplicates: activeTab === "duplicates" ? [] : data.duplicates,
      missing: activeTab === "missing" ? [] : data.missing,
    };
    try {
      await put.mutateAsync(payload);
      toast.success(`Cleared ${activeTab} list`);
      setConfirmClearOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }

  if (isLoading) return <InventorySkeleton />;
  if (isError) {
    return (
      <div className="text-center py-20">
        <p className="text-[#FF6B6B] font-body text-sm" data-testid="inventory-error">
          Failed to load your inventory.
        </p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 h-10 rounded-md border border-white/15 text-white font-body text-sm hover:bg-white/5"
        >
          Retry
        </button>
      </div>
    );
  }

  const stats = data.stats;
  const activeCount =
    activeTab === "duplicates" ? stats.duplicates_count : stats.missing_count;

  return (
    <div data-testid="inventory-page" className="space-y-8 max-w-6xl mx-auto">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
          My Inventory
        </h1>
        <p className="font-body text-sm text-[#A1A1AA] mt-1">
          Tap a chip to add it to the active list. Numbers move freely between
          Duplicates and Missing.
        </p>
      </header>

      {/* Stat tiles */}
      <section
        data-testid="stat-tiles"
        className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      >
        <StatTile
          testid="stat-duplicates"
          icon={<Copy className="h-4 w-4" />}
          label={t("inventory.stat_duplicates")}
          value={stats.duplicates_count}
          accent="solid"
        />
        <StatTile
          testid="stat-missing"
          icon={<CircleDashed className="h-4 w-4" />}
          label={t("inventory.stat_missing")}
          value={stats.missing_count}
          accent="outline"
        />
        <div
          data-testid="stat-progress"
          className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 sm:p-5 col-span-2 lg:col-span-1"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-xs uppercase tracking-wider font-body">
              <CheckCircle2 className="h-4 w-4 text-[#E5B83A]" />
              {t("inventory.stat_progress")}
            </div>
            <span className="font-display text-base font-bold text-white">
              {TOTAL - stats.missing_count} <span className="text-[#A1A1AA] font-normal">/ {TOTAL}</span>
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#E5B83A] to-[#C9971C] transition-all duration-500"
              style={{ width: `${stats.completion_pct}%` }}
              data-testid="progress-bar"
            />
          </div>
          <p className="font-body text-xs text-[#A1A1AA]/80 mt-2">
            {stats.completion_pct}% complete · {stats.missing_count} to go
          </p>
        </div>
        <StreakTile />
      </section>

      {/* Tab switcher */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          data-testid="inventory-tabs"
          className="flex w-full h-auto p-0 bg-transparent border-b border-white/10 rounded-none"
        >
          <TabsTrigger
            value="duplicates"
            data-testid="tab-duplicates"
            className="flex-1 pb-3 pt-1 text-sm font-medium bg-transparent rounded-none text-[#A1A1AA] data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E5B83A] transition-all"
          >
            {t("inventory.stat_duplicates")} ({stats.duplicates_count})
          </TabsTrigger>
          <TabsTrigger
            value="missing"
            data-testid="tab-missing"
            className="flex-1 pb-3 pt-1 text-sm font-medium bg-transparent rounded-none text-[#A1A1AA] data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E5B83A] transition-all"
          >
            {t("inventory.stat_missing")} ({stats.missing_count})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Quick-add */}
      <form
        onSubmit={handleQuickAdd}
        className="flex flex-col sm:flex-row gap-3 items-stretch"
        data-testid="quick-add-form"
      >
        <div className="flex-1">
          <label className="sr-only" htmlFor="quick-add-input">
            Quick add numbers
          </label>
          <input
            id="quick-add-input"
            data-testid="quick-add-input"
            value={quickValue}
            onChange={(e) => setQuickValue(e.target.value)}
            placeholder={`e.g. 12, 45 88 200-205 — adds to ${activeTab}`}
            className="w-full h-11 bg-[#121212] border border-white/10 rounded-md px-3 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A]"
          />
          {quickError ? (
            <p
              data-testid="quick-add-error"
              className="text-xs text-[#FF6B6B] font-body mt-1.5"
            >
              {quickError}
            </p>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={put.isPending || !quickValue.trim()}
          data-testid="quick-add-submit"
          className="h-11 px-5 rounded-md bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black font-semibold text-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2"
        >
          {put.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Add
        </button>
        {/* Phase 21 — FiguScan trigger. min 44×44 tap target (h-11 = 44px). */}
        <button
          type="button"
          onClick={() => setScanOpen(true)}
          data-testid="figuscan-open"
          className="h-11 px-5 rounded-md bg-transparent border border-[#E5B83A]/60 text-[#E5B83A] font-semibold text-sm hover:bg-[#E5B83A]/10 transition-all inline-flex items-center justify-center gap-2"
        >
          <Camera className="h-4 w-4" />
          {t("figuscan.button_label", "Scan Sticker")}
        </button>
      </form>

      <FiguScanModal open={scanOpen} onOpenChange={setScanOpen} />

      {/* 650-chip grid */}
      <section
        id="inventory-grid"
        data-testid="sticker-grid"
        className="grid gap-1.5 sm:gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(36px, 1fr))" }}
      >
        {ALL_NUMBERS.map((n) => (
          <StickerChip
            key={n}
            number={n}
            state={getState(n)}
            grade={data?.sticker_grades?.[String(n)] ?? null}
            onClick={handleChipClick}
          />
        ))}
      </section>

      {/* Clear list */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={() => setConfirmClearOpen(true)}
          disabled={activeCount === 0 || put.isPending}
          data-testid="clear-list-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-white/15 text-[#A1A1AA] font-body text-sm hover:bg-white/5 hover:border-[#FF6B6B]/40 hover:text-[#FF6B6B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Eraser className="h-4 w-4" />
          Clear {activeTab} list
        </button>
      </div>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent
          data-testid="clear-list-dialog"
          className="bg-[#0A0A0A] border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Clear your {activeTab} list?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA] font-body">
              This removes all {activeCount} number{activeCount === 1 ? "" : "s"} from your{" "}
              {activeTab} list. You can add them back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="clear-list-cancel"
              className="bg-transparent border border-white/15 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearList}
              data-testid="clear-list-confirm"
              className="bg-[#FF6B6B] text-white hover:bg-[#FF8585]"
            >
              Clear list
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StreakTile() {
  const { data, isLoading } = useStreak();
  const wrapRef = useRef(null);

  // Fire the pulse animation once per UTC day per session.
  useEffect(() => {
    if (!data || !data.counted_today) return;
    const date = data.last_activity_date;
    const key = `figumatch_streak_pulsed_${date}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const el = wrapRef.current;
    if (el) {
      el.classList.add("figumatch-streak-pulse");
      const t = setTimeout(() => el.classList.remove("figumatch-streak-pulse"), 2700);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <div
        data-testid="stat-streak"
        className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 sm:p-5 flex items-center gap-4"
      >
        <div className="h-10 w-10 rounded-md bg-white/5 animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-16 bg-white/5 rounded animate-pulse" />
          <div className="h-6 w-12 bg-white/5 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const current = data.current_streak || 0;
  const longest = data.longest_streak || 0;
  const empty = current === 0 && !data.last_activity_date;
  const showBest = longest > current && longest > 0;

  return (
    <div
      ref={wrapRef}
      data-testid="stat-streak"
      className={
        "bg-[#0A0A0A] border border-white/10 rounded-lg p-4 sm:p-5 flex items-center gap-4 relative transition-all " +
        (empty ? "opacity-60" : "")
      }
    >
      <div className="h-10 w-10 rounded-md border border-[#E5B83A] text-[#E5B83A] flex items-center justify-center shrink-0">
        <Flame className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body">
          Streak
        </p>
        {empty ? (
          <p className="font-body text-sm text-[#A1A1AA] mt-0.5" data-testid="streak-empty">
            Start your streak
          </p>
        ) : (
          <div className="flex items-baseline gap-1.5">
            <p className="font-display text-2xl font-bold text-white mt-0.5" data-testid="streak-value">
              {current}
            </p>
            <span className="font-body text-xs text-[#A1A1AA]">
              {current === 1 ? "day" : "days"}
            </span>
          </div>
        )}
        {showBest ? (
          <p className="font-body text-[10px] text-[#A1A1AA]/70 mt-0.5" data-testid="streak-best">
            Best: {longest}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 sm:p-5 flex items-center gap-4"
    >
      <div
        className={
          "h-10 w-10 rounded-md flex items-center justify-center " +
          (accent === "solid"
            ? "bg-[#E5B83A] text-black"
            : "border border-[#E5B83A] text-[#E5B83A]")
        }
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body">
          {label}
        </p>
        <p className="font-display text-2xl font-bold text-white mt-0.5">
          {value}
        </p>
      </div>
    </div>
  );
}

function InventorySkeleton() {
  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="inventory-loading">
      <div className="h-10 w-48 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-white/5 rounded-lg animate-pulse" />
        ))}
      </div>
      <div className="h-9 bg-white/5 rounded animate-pulse" />
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(36px, 1fr))" }}
      >
        {Array.from({ length: 60 }).map((_, i) => (
          <div key={i} className="h-9 bg-white/5 rounded-md animate-pulse" />
        ))}
      </div>
    </div>
  );
}
